import React, { memo, useCallback, useEffect, useState } from 'react';

import { InView } from 'react-intersection-observer';
import { useDispatch, useSelector } from 'react-redux';

import dynamic from 'next/dynamic';

import qs from 'querystring';

import clsx from 'clsx';
import _chunk from 'lodash/chunk';
import _get from 'lodash/get';
import _isEqual from 'lodash/isEqual';

import { useDevice } from '@bonnet/next/device';
import { fetchJSON } from '@bonnet/next/fetch';

import { getPath, pageNames } from '@atc/bonnet-paths';
import { brands, useBrand } from '@atc/brand';
import { useFeatures } from '@atc/features';
import { formatNumber } from '@atc/string-fns';

import { Button, SubHeading } from 'reaxl';
import { sendClick, sendImpressions } from 'reaxl-analytics';
import { InventoryListingPlaceholder } from 'reaxl-listing';

import configureListingTiles from '@/utilities/configureListingTiles';
import { listingDataPreprocessing } from '@/utilities/dispatchUtils';
import getVehicleHighlightWithFallback from '@/utilities/getVehicleHighlightWithFallback';

import { seeMoreButtonClick } from '@/analytics/srp/infiniteScrollAnalytics';
import { inventoryImpressions } from '@/analytics/srpAnalyticsHandlers';

import {
    authDuck,
    cpoContentDuck,
    inventoryDuck,
    ownersDuck,
    queryDuck,
    userDuck,
} from '@/ducks';

import {
    srpFiltersDuck,
    srpLocationTypeDuck,
    srpNewCarBoostDuck,
    srpPaginationDuck,
    srpPrimeSpotlightDuck,
    srpResultsDuck,
    srpSortDuck,
    srpSpotlightDuck,
    srpSupplementalDuck,
} from '@/ducks/srp';

import LazyComponent from '@/components/LazyComponent';

import InlineFilterPromoCard from './InlineFilterPromoCard';
import InventoryListingContainer from './InventoryListingContainer';
import InventorySpecialListing from './InventorySpecialListing';
import ListingsLinerAds from './ListingsLinerAds';
import MyWalletGridContainer from './MyWalletGridContainer';
import SimilarInventoryContainerPlaceholder from './SimilarInventoryContainerPlaceholder';
import SRPDealerDiffModalWrapper from './SRPDealerDiffModalWrapper';
import SRPJumpLink from './SRPJumpLink';
import SRPListingCollection from './SRPListingCollection';
import SupplementalSearchPlaceholder from './SupplementalSearchPlaceholder';
import WalletInputCardContainer from './WalletInputCardContainer';

// lazy
export const PreorderSearchContainer = dynamic(() => import(
    /* webpackChunkName: "PreorderSearchContainer" */
    '@/containers/srp/PreorderSearchContainer'
), {
    ssr: false,
    loading: () => (
        <InventoryListingPlaceholder
            className="col-xs-12 col-sm-4"
            data-cmp="inv-placeholder-preorder"
            key="preorder-inventory-placeholder"
        />
    ),
});
export const SupplementalSearchContainer = dynamic(() => import(
    /* webpackChunkName: "SupplementalSearchContainer" */
    '@/containers/srp/SupplementalSearchContainer'
), {
    ssr: false,
    loading: () => (<SupplementalSearchPlaceholder />),
});
export const SimilarInventoryContainer = dynamic(() => import(
    /* webpackChunkName: "SimilarInventoryContainer" */
    '@/containers/srp/SimilarInventoryContainer'
), {
    ssr: false,
    loading: () => (<SimilarInventoryContainerPlaceholder />),
});
export const SpotlightCarouselContainer = dynamic(() => import(
    /* webpackChunkName: "SpotlightCarouselContainer" */
    '@/containers/srp/SpotlightCarouselContainer'
), { ssr: false });

const { VEHICLE_DETAILS } = pageNames;

/*
 * render list of inventory listings as spotlights, regular and supplemental listings
 */
function InventorySearchResultsContainer() {

    const dispatch = useDispatch();
    const device = useDevice();
    const { brand, isBrand } = useBrand();
    const isKbbBranded = isBrand(brands.KBB_BRAND);
    const isFBABranded = isBrand(brands.FORD_BRAND);

    const listings = useSelector((state) => srpResultsDuck.selectors.getActiveInventory(state));
    const resultCount = useSelector(srpResultsDuck.selectors.getResultsCount);
    const searchRadius = useSelector(srpFiltersDuck.selectors.getSelectedSearchRadius);
    const sort = useSelector(srpSortDuck.selectors.getDuckState);
    const spotlights = useSelector(srpSpotlightDuck.selectors.getActiveInventory, _isEqual);
    const vehicleHighlightListings = useSelector(srpPrimeSpotlightDuck.selectors.getActiveInventory, _isEqual);
    const isResultsLoading = useSelector(srpResultsDuck.selectors.getResultLoading);
    const boostListings = useSelector(srpNewCarBoostDuck.selectors.getAvailableInventory, _isEqual);
    const supplementalResultsCount = useSelector(srpSupplementalDuck.selectors.getResultCount);
    const supplementalResultsLoaded = useSelector(srpSupplementalDuck.selectors.getLoadedCount);
    const supplementalInitialLoad = useSelector(srpSupplementalDuck.selectors.getInitialLoad);
    const supplementalLoading = useSelector(srpSupplementalDuck.selectors.getLoading);
    const certifiedSponsor = useSelector(cpoContentDuck.selectors.selectCertifiedContentSimple);
    const query = useSelector(queryDuck.selectors.getDuckState);
    const isMaxActiveResults = useSelector(srpResultsDuck.selectors.getIsMaxActiveResults);

    const isLoggedIn = useSelector(authDuck.selectors.isLoggedIn);
    const leadProfile = useSelector(userDuck.selectors.getLeadProfile);

    const {
        vehicleHighlightListingsFinal = [],
        vehicleHighlightCount = 0,
    } = getVehicleHighlightWithFallback(vehicleHighlightListings);
    // use first vehicle highlight listing for boo listing
    const [vehicleHighlightBooListing, ...rest] = vehicleHighlightListingsFinal;
    const vehicleHighlightCarouselListings = vehicleHighlightCount === 1
        ? vehicleHighlightListingsFinal
        : rest;

    const isNationalSRP = useSelector(srpLocationTypeDuck.selectors.getDuckState);

    const currentPage = useSelector(srpPaginationDuck.selectors.getCurrentPage);
    let resultsPerPage = useSelector(srpPaginationDuck.selectors.getNumRecords);
    if (isFBABranded && resultsPerPage === 25) {
        resultsPerPage = 30;
    }
    const isLastPage = currentPage >= Math.ceil(resultCount / resultsPerPage);
    const isLg = _get(device, 'is.lg', true);

    useEffect(() => {
        if (userDuck && !leadProfile) {
            dispatch(userDuck.creators.setLeadProfile());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const {
        brand: [, { inventory_image_url: inventoryImageUrl }],
        enable_new_car_boost: [enableNewCarBoost],
        experience_query_params: [enableExperienceQueryParams, { queryParams }],
        max_listing_count: [enableMaxListingCount],
        fluid_ad: [enableFluidAd],
        my_wallet: [enableMyWallet],
        non_relevant_sort_spotlights: [enableSpotlightNonRelevantSort, {
            before_mee: showSpotlightsBeforeMEE,
            bottom_page: showSpotlightsBottomPage,
            hide_spotlights: hideSpotlights,
        }],
        override_carfax_tile: [isOverrideCarfaxTileEnabled, { partner }],
        premium_spotlight: [enablePremiumSpotlight, {
            carousel_row_placement: carouselRowPlacement,
            peekaboo_listing_placement: peekabooListingPlacement,
        }],
        remove_carfax_tile: [removeCarfaxTile],
        remove_experian_tile: [removeExperianTile],
        similar_listings_srp: [enableSimilarListings],
        srp_bottom_lazy_threshold: [, { defaultThreshold }],
        srp_infinite_scroll: [, {
            button: isButtonScroll,
            trigger: isTriggerScroll,
            rootMargin = 1000,
        }],
        srp_inline_filters: [enableInlineFilters],
        srp_jump_link: [enableSrpJumpLink],
        srp_liner_ad: [, {
            hide_liner_ad1: hideLinerAd1,
            hide_liner_ad2: hideLinerAd2,
            hide_liner_ad3: hideLinerAd3,
            hide_liner_ad4: hideLinerAd4,
            hide_liner_ad5: hideLinerAd5,
        }],
        srp_wallet_grid_promo_card: [enableNewWalletPromoCard],
        standard_listing_owner_name: [hideOwnerName],
        standard_listing_phone_display: [displayPhoneNumber],
        supplemental: [supplementalEnabled],
        vehicle_highlight: [isVehicleHighlightEnabled],
    } = useFeatures([
        'brand',
        'enable_new_car_boost',
        'experience_query_params',
        'max_listing_count',
        'fluid_ad',
        'my_wallet',
        'non_relevant_sort_spotlights',
        'override_carfax_tile',
        'premium_spotlight',
        'remove_carfax_tile',
        'remove_experian_tile',
        'schema',
        'similar_listings_srp',
        'spotlight',
        'srp_bottom_lazy_threshold',
        'srp_infinite_scroll',
        'srp_inline_filters',
        'srp_jump_link',
        'srp_liner_ad',
        'srp_wallet_grid_promo_card',
        'standard_listing_owner_name',
        'standard_listing_phone_display',
        'supplemental',
        'vehicle_highlight',
    ]);
    const [showSimilarListings, setShowSimilarListings] = useState(false);

    const hasSearchRadius = !!searchRadius;

    const shouldLoadSimilarListings = !isNationalSRP && hasSearchRadius && enableSimilarListings;

    const enableVehicleHighlights = enablePremiumSpotlight && isVehicleHighlightEnabled;

    useEffect(() => {
        if (!shouldLoadSimilarListings) {
            return;
        }

        if (showSimilarListings && supplementalResultsCount === 0 && supplementalResultsLoaded === 0 && supplementalInitialLoad) {
            // detect that srpSupplementalDuck has been cleared on SupplementalSearchContainer useEffect
            setShowSimilarListings(false);
        } else if (!showSimilarListings && !supplementalLoading && !supplementalInitialLoad && !isResultsLoading) {
            const isLowResultCount = resultCount + supplementalResultsCount <= 25;
            const supplementalListingsRendered = supplementalResultsCount > 0 && ((supplementalResultsLoaded >= supplementalResultsCount) || supplementalResultsLoaded === 100);
            const noSupplementalLoaded = !supplementalInitialLoad && !supplementalLoading && supplementalResultsCount === 0;
            const isSupplementalDoneLoading = supplementalListingsRendered || noSupplementalLoaded;
            if (enableSimilarListings && isLowResultCount && isSupplementalDoneLoading && hasSearchRadius) {
                setShowSimilarListings(true);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [supplementalResultsCount, supplementalResultsLoaded, supplementalInitialLoad, supplementalLoading, resultCount, showSimilarListings, isResultsLoading, isNationalSRP, shouldLoadSimilarListings]);

    // Clean up eventBus and script tag when component unmount
    useEffect(() => {
        ['psx.listingActions.openMakeOffer', 'psx.listingActions.makeOfferModalIsLoaded'].forEach(window.caiEventBus.off);
        document.querySelector('script[src*="static.kbb.com/psx-listing-actions"]')?.remove();
    }, []);

    // Show supplemental results condition
    const showSupplementalResults = isLastPage && hasSearchRadius;

    // Retrieve saved inventory ids and set them in the inventory reducer
    const initializeUserInfo = useCallback(() => {
        dispatch(userDuck.creators.hydrateUserInventory({ isLoggedIn, onLoggedInStateReady: () => { } }));
    }, [dispatch, isLoggedIn]);

    // Retrieve recently viewed inventory ids and set them in the inventory reducer
    const initializeRecentInventory = useCallback(() => {
        dispatch(userDuck.creators.hydrateRecentInventory({ isLoggedIn, onLoggedInStateReady: () => { } }));
    }, [dispatch, isLoggedIn]);

    useEffect(() => {
        initializeRecentInventory();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /*
      * This useEffect hook is intended to fire only on component mount
      * and will request a fourth spotlight if necessary as well as
      * fire all initial page-loaded spotlight listings
      */
    useEffect(() => {
        // Initialize saved listing and other user data
        // Only call this function on KBB
        if (isKbbBranded) {
            initializeUserInfo();
        } // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const listingsWithTiles = configureListingTiles({
        brand,
        certifiedSponsor,
        items: listings,
        isOverrideCarfaxTileEnabled,
        partner,
        tier: 'listing',
        removeCarfaxTile,
        removeExperianTile,
    });

    const renderFluidAd = (index) => (
        <div
            className="col-xs-12 col-sm-4 display-flex justify-content-center"
            key={`fluid-ad-${index}`}
        >
            <div
                className="panel panel-default border-0"
                style={{ marginTop: -17 }}
            >
                <ListingsLinerAds
                    className="test border-0"
                    index={index}
                />
            </div>
        </div>
    );

    // TODO: maybe just set to invisible instead of unmounting when there are not enough listings?
    const renderLinerAd = (index) => (
        <div
            className="col-xs-12 margin-bottom-5"
            key={`liner-ad-${index}`}
        >
            <ListingsLinerAds
                index={index}
            />
        </div>
    );

    const [infiniteScrollButtonPressed, setInfiniteScrollButtonPressed] = useState(false);

    const fetchMoreData = async () => {
        const firstRecord = listings.length;
        const searchQuery = {
            ...query,
            firstRecord,
        };

        if (enableExperienceQueryParams) {
            Object.assign(searchQuery, { ...queryParams });
        }

        const listingsEndpoint = '/rest/lsc/listing';

        const { listings: activeListings = [], owners = [] } = await fetchJSON(`${listingsEndpoint}?${qs.stringify(searchQuery)}`);
        const vdpUrl = await getPath(VEHICLE_DETAILS, {}, {
            useUpdatedVdpPath: true,
        });

        activeListings.forEach((fetchedListing) => {
            listingDataPreprocessing({
                listing: fetchedListing,
                owners,
                displayPhoneNumber,
                hideOwnerName,
                inventoryImageUrl,
                vdpUrl,
            });
        });

        if (activeListings?.length) {
            dispatch(inventoryDuck.creators.addInventory(activeListings));
            dispatch(ownersDuck.creators.addOwners(owners));
            dispatch(srpResultsDuck.creators.addActiveResults(activeListings.map((item) => item.id)));
        }

        sendImpressions({
            name: inventoryImpressions,
            data: {
                cmp: 'lstg',
                startingIndex: firstRecord,
                selectedItemIds: activeListings.map((listing) => listing.id),
                pixallData: { rank: firstRecord },
            },
        });
        setInfiniteScrollButtonPressed(true);
    };

    const searchResultsListings = listingsWithTiles?.map((listing, index) => {
        const paginatedListingIndex = (index + 1) + ((currentPage - 1) * resultsPerPage);
        return (
            <InventoryListingContainer
                key={`inventory-container-${index}`}
                listing={listing}
                index={index}
                paginatedListingIndex={paginatedListingIndex}
            />
        );
    });

    /*
     * Inject 4 Spotlights into the SRP results if available:
     * 2 Premium spotlights between the Alpha and the search results
     * 1 Premium spotlight after the 5th listing of the search results
     * 1 Non-Premium spotlight after the 20th listing. Show a Featured over a Standard spotlight if there are the same
     *  or more featured than standard organic listings
     */
    /* eslint-disable no-unused-expressions*/
    // insert spotlights at specific indexes

    const isRelevantSort = sort === 'relevance';
    // hide spotlights for non-relevant sort
    if (!enableSpotlightNonRelevantSort || ((hideSpotlights || enableSpotlightNonRelevantSort) && isRelevantSort)) {
        [0, 1, 2, 23].forEach((position, index) => {
            spotlights?.[index]?.id && searchResultsListings.splice(position, 0, <InventorySpecialListing listingIndex={index} />);
        });
    }
    // show spotlights at the bottom of the page for non-relevant sort
    if ((showSpotlightsBottomPage || (showSpotlightsBeforeMEE && isLastPage)) && !isRelevantSort) {
        spotlights?.slice(0, 4).forEach((_, index) => {
            searchResultsListings.push(
                <InventorySpecialListing listingIndex={index} />
            );
        });
    }

    /*
    * Inject My Wallet promo grid placement at spot #4
        will be injected in a row separately when higher peekaboo variant is on
    */
    if (enableNewWalletPromoCard) {
        searchResultsListings.splice(3, 0, <WalletInputCardContainer key="WalletInputCardContainer" />);
    }

    if (vehicleHighlightBooListing) {
        searchResultsListings.splice(Number(peekabooListingPlacement) || 8, 0, <InventorySpecialListing
            listingIndex={8}
            listingCategory="peekaboo"
        />);
    }

    /*
     * Inject 2 New Car Boost listings if available
     * first one at index 6 (spot #7) or the first listing in the 3rd row on desktop
     * second at index 21 if there are 75 or more results per page
     */
    if (enableNewCarBoost) {
        if (boostListings[0]) {
            searchResultsListings.splice(6, 0, <InventorySpecialListing
                listingIndex={0}
                listingCategory="boost"
            />);
            // Ensure there is enough listing slots to fill the row before splicing into results
            // We will dispatch an extra listing if there is no NCB listing to serve as a fallback/placeholder
            // To prevent CLS
        }

        if (resultsPerPage >= 75 && resultCount >= 75) {
            boostListings[1] && searchResultsListings.splice(22, 0, <InventorySpecialListing
                listingIndex={1}
                listingCategory="boost"
            />);
        }
    }

    /*
     * Inject InlineFilterPromoCard placement at spot #15
     */
    if (searchResultsListings.length > 14 && enableInlineFilters) {
        searchResultsListings.splice(
            14,
            0,
            (
                <InlineFilterPromoCard
                    key="inlineFilterPromoCard"
                />
            ),
        );
    }

    // TODO we need a fallback for this once they actually start selling the product enough to matter (or fill with other listings)
    const renderVehicleHighlightCarousel = (
        <LazyComponent
            key="spotlight-carousel-placeholder-3"
            height={435}
            componentProps={{ className: 'margin-bottom-5 col-xs-12' }}
        >
            {vehicleHighlightCarouselListings.length > 0
                && <SpotlightCarouselContainer spotlights={vehicleHighlightCarouselListings} />}
        </LazyComponent>
    );

    if (enableFluidAd) {
        !hideLinerAd1 && searchResultsListings.length >= 7 && searchResultsListings.splice(7, 0, renderFluidAd(0));
        !hideLinerAd2 && searchResultsListings.length >= 14 && searchResultsListings.splice(14, 0, renderFluidAd(1));
        !hideLinerAd3 && searchResultsListings.length >= 19 && searchResultsListings.splice(19, 0, renderFluidAd(2));
        !hideLinerAd4 && searchResultsListings.length >= 36 && searchResultsListings.splice(36, 0, renderFluidAd(3));
        !hideLinerAd5 && searchResultsListings.length >= 43 && searchResultsListings.splice(43, 0, renderFluidAd(4));
    }

    // wrap every listing after the 3rd one in LazyComponent
    const searchResultsLazyListings = [];
    searchResultsListings.forEach((listing, index) => {
        if (index < 3 || listing?.key === 'myWalletPromoCard') {
            searchResultsLazyListings.push(listing);
        } else {
            searchResultsLazyListings.push(
                <LazyComponent
                    componentClass={InventoryListingPlaceholder}
                    componentProps={{ className: 'col-xs-12 col-sm-4' }}
                    key={`inventory-placeholder-${index}`}
                >
                    {listing}
                </LazyComponent>
            );
        }
    });

    // chunk all search results listings in sets of 3 representing each row [[item, item, item], [item, item, item], ...]
    const chunkedListingsSet = _chunk(searchResultsLazyListings, 3);

    // insert 3-column-wide components at specific indexes of chunkedListingsSet
    if (enableVehicleHighlights && searchResultsListings.length > 6) {
        chunkedListingsSet.splice(Number(carouselRowPlacement) || 1, 0, [renderVehicleHighlightCarousel]);
    }

    if (!enableFluidAd) {
        !hideLinerAd1 && searchResultsListings.length > 6 && chunkedListingsSet.splice(3, 0, renderLinerAd(0));
        !hideLinerAd2 && searchResultsListings.length > 14 && chunkedListingsSet.splice(7, 0, renderLinerAd(1));
        !hideLinerAd3 && searchResultsListings.length > 19 && chunkedListingsSet.splice(10, 0, renderLinerAd(2));
        !hideLinerAd4 && searchResultsListings.length > 33 && chunkedListingsSet.splice(13, 0, renderLinerAd(3));
        !hideLinerAd5 && searchResultsListings.length > 36 && chunkedListingsSet.splice(16, 0, renderLinerAd(4));
    }

    const showWalletGrid = currentPage === 1 || resultCount === 0;
    const renderMyWalletGrid = (enableMyWallet && showWalletGrid) && (
        <MyWalletGridContainer
            uiContext="default"
            showIcon={isLg}
        />
    );

    return (
        <div data-cmp="cntnr-listings">
            <SubHeading
                componentClass="h2"
                className={clsx({
                    'margin-top-2': enableMaxListingCount,
                    'padding-bottom-4': !enableMaxListingCount,
                })}
                size={enableMaxListingCount ? 300 : 500}
                data-cmp="resultsCount"
                aria-live="assertive"
                aria-atomic="true"
                id="results-count"
                style={{ scrollMargin: '30px' }}
            >
                {formatNumber({ value: resultCount }) + (enableMaxListingCount ? ' Matches ' : ' Results')}
                {enableSrpJumpLink && <SRPJumpLink />}
            </SubHeading>

            <div className="row display-flex flex-wrap margin-top-3">
                {chunkedListingsSet.slice(0, 1).flat().map((item) => item)}

                {renderMyWalletGrid}

                {chunkedListingsSet.slice(1, chunkedListingsSet.length).flat().map((item) => item)}

                {isButtonScroll && !infiniteScrollButtonPressed && !isMaxActiveResults && (
                    <Button
                        bsStyle="primary"
                        className="col-xs-12 margin-vertical-5 text-center"
                        onClick={async (event) => {
                            sendClick(seeMoreButtonClick, event, {});
                            fetchMoreData();
                        }}
                    >
                        Load More Results
                    </Button>
                )}

                {(isTriggerScroll || infiniteScrollButtonPressed) && !isMaxActiveResults && (
                    <InView
                        as="div"
                        rootMargin={`${rootMargin}px`}
                        onChange={() => {
                            // We need to check for scrollY to be greater than 0 to avoid triggering more listings
                            // when googlebot loads with their huge viewport, since this would prevent them from
                            // seeing all the SEO content at the bottom of the page.
                            window?.scrollY > 0 && fetchMoreData();
                        }}
                        className="col-xs-12 margin-vertical-5 text-center"
                    >
                        <h3>Loading more results...</h3>
                    </InView>
                )}

                {isLastPage && <PreorderSearchContainer />}

                {isLastPage && supplementalEnabled && (
                    <SupplementalSearchContainer
                        listingsWithTilesLength={listingsWithTiles.length}
                        showSupplementalResults={showSupplementalResults}
                        isLastPage={isLastPage}
                    />
                )}

                {shouldLoadSimilarListings && isLastPage && (showSimilarListings || (!supplementalEnabled && resultCount < 25)) && (
                    <LazyComponent
                        threshold={defaultThreshold}
                        fallback={(<SimilarInventoryContainerPlaceholder />)}
                    >
                        <SimilarInventoryContainer
                            searchRadius={searchRadius}
                            isLastPage={isLastPage}
                        />
                    </LazyComponent>
                )}

                <SRPListingCollection />
            </div>

            <SRPDealerDiffModalWrapper />

            <lead-generation
                layout="srpListing"
                is-director
            />
        </div>
    );
}

export default memo(InventorySearchResultsContainer, _isEqual);
